import Storage from '@octadesk-tech/storage'

import store from '@/store'

import { getServerDate } from '../serverdate'

import { orderby } from '@/helpers'

import { postMessage } from './iframe'

const pagesKey = 'pages'
const fluxesKey = 'fluxes'
const settingsKey = 'settings'
const cultureKey = 'culture'
const hasTicketAppKey = 'hasTicketApp'
const activeKey = 'active'
const chatCacheKey = 'chatKey'
const messagesKey = 'messages'
const userIdKey = 'userId'
const cachedUserKey = 'cachedUser'
const offsetKey = 'offset'

const storageKeyName = ({ key, includeFluxId = true }) => {
  const prefix = `chat_${store.getters.company}_${key}`

  if (!includeFluxId) return prefix

  const { options, flux } = store.getters

  const scriptId =
    options && options.id && options.id !== 'chat' ? `${options.id}_` : ''

  const fluxId = flux && flux.id ? `${flux.id}` : ''

  return `${prefix}_${scriptId}${fluxId}`
}

export const get = key =>
  Storage.getItem(storageKeyName({ key, includeFluxId: false }))

const safeStorageSetItem = (key, data, expiration = undefined) => {
  try {
    Storage.setItem(key, data, expiration)

    return true
  } catch (error) {
    if (error.name === 'QuotaExceededError') {
      console.warn(`⚠️ localStorage full, cannot save ${key}`)

      return false
    }

    console.error(`Error trying to save to localStorage ${key}:`, error)

    return false
  }
}

export const set = (key, data, expiration = 600) =>
  safeStorageSetItem(
    storageKeyName({ key, includeFluxId: false }),
    data,
    expiration
  )

export const setWithContext = (key, data) =>
  safeStorageSetItem(storageKeyName({ key }), data)

export const getWithContext = key => Storage.getItem(storageKeyName({ key }))

export const removeWithContext = key =>
  Storage.removeItem(storageKeyName({ key }))

export const setSessionActive = () =>
  setWithContext(activeKey, store.getters.active)

export const getSessionActive = () => getWithContext(activeKey)

export const removeSessionActive = () => removeWithContext(activeKey)

export const setSessionChatKey = () => {
  const { chatKey } = store.getters

  if (chatKey && chatKey.length) {
    setWithContext(chatCacheKey, chatKey)

    postMessage({
      name: 'setSession',
      sessionId: chatKey
    })
  } else {
    removeWithContext(chatCacheKey)
  }
}

export const getSessionChatKey = () => getWithContext(chatCacheKey)

export const getSessionMessages = () => getWithContext(messagesKey) || []

export const updateSessionMessage = message => {
  const sessionMessages = getSessionMessages()

  const messages = sessionMessages.filter(m => m.key !== message.key)

  messages.push(message)

  const MAX_MESSAGES = 10

  const limitedMessages = messages.slice(-MAX_MESSAGES)

  setWithContext(messagesKey, limitedMessages)
}

export const removeSessionMessages = messages => {
  const sessionMessages = getSessionMessages()

  const newMessages = sessionMessages.filter(
    sm => !messages.some(m => m.key === sm.key)
  )

  setWithContext(messagesKey, newMessages)
}

export const clearSessionMessages = () => removeWithContext(messagesKey)

const sortAndSlicePages = pages =>
  orderby.desc(pages, item => item.enterDate).slice(0, 20)

export const getSessionPages = () =>
  sortAndSlicePages(getWithContext(pagesKey) || [])

export const addSessionPage = page => {
  const pages = getSessionPages()

  setWithContext(pagesKey, [...pages, page])
}

export const clearSessionPages = () => removeWithContext(pagesKey)

export const getUserId = () => getWithContext(userIdKey)

export const setUserId = id => setWithContext(userIdKey, id)

export const clearSession = () => {
  for (let item of [
    activeKey,
    messagesKey,
    chatCacheKey,
    pagesKey,
    userIdKey,
    cachedUserKey
  ]) {
    removeWithContext(item)
  }

  try {
    const keysToRemove = []

    for (let key in localStorage) {
      if (
        Object.hasOwn(localStorage, key) &&
        (key.startsWith('roomToken-') || key.startsWith('room-'))
      ) {
        keysToRemove.push(key)
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key))
  } catch (error) {
    console.error('Error trying to clear session (roomToken and room):', error)
  }
}

export const setFluxes = data => set(fluxesKey, data)

export const getFluxes = () => get(fluxesKey)

export const setChatSettings = settings => set(settingsKey, settings)

export const getChatSettings = () => get(settingsKey)

export const setCulture = data => set(cultureKey, data)

export const getCulture = () => get(cultureKey)

export const getHasTicketApp = () => get(hasTicketAppKey)

export const setHasTicketApp = val => set(hasTicketAppKey, val)

export const now = async () => {
  let cachedOffset = get(offsetKey)

  if (cachedOffset === null) {
    let offset = 0

    try {
      const serverDate = await getServerDate()

      offset = serverDate.offset
    } catch (ex) {
      console.error('error on serverdate: ', ex.msg)
    }

    cachedOffset = offset

    set(offsetKey, cachedOffset)
  }

  return new Date(Date.now() + cachedOffset)
}

export const getCachedUser = () => getWithContext(cachedUserKey)

export const setCachedUser = user => setWithContext(cachedUserKey, user)

export const clearCachedUser = () => removeWithContext(cachedUserKey)

window.onbeforeunload = () => {
  const { company, page } = store.getters

  if (!company || !company.length) return

  if (page) {
    page.leaveDate = new Date()

    addSessionPage(page)
  }
}
